import { useTranslation } from 'react-i18next'
import { FiBookOpen, FiExternalLink, FiZap } from 'react-icons/fi'
import Card, { CardBody, CardHeader } from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'

export interface CourtRuling {
  id: string
  category: string
  case_no?: string | null
  court_name?: string | null
  ruling_date?: string | null
  title: string
  summary?: string | null
  brief?: {
    summary?: string
    key_finding?: string
    implications?: string[]
    related_laws?: string[]
    practical_tip?: string
  } | null
  source_url: string
  source_attribution?: string | null
  published_date: string
}

const CATEGORY_LABEL: Record<string, { ko: string; en: string; color: 'primary' | 'warning' | 'danger' }> = {
  serious_accident_act: { ko: '중대재해처벌법', en: 'Serious Accident Act', color: 'danger' },
  workers_comp: { ko: '산재보험', en: 'Workers Compensation', color: 'primary' },
  osh_law: { ko: '산업안전보건법', en: 'OSH Law', color: 'warning' },
}

interface Props {
  ruling: CourtRuling
}

export default function CourtRulingCard({ ruling }: Props) {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'ko').startsWith('en') ? 'en' : 'ko'
  const catMeta = CATEGORY_LABEL[ruling.category] ?? { ko: ruling.category, en: ruling.category, color: 'primary' as const }
  const b = ruling.brief

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge variant={catMeta.color}>
                <FiBookOpen size={10} className="inline mr-0.5" />
                {lang === 'ko' ? catMeta.ko : catMeta.en}
              </Badge>
              {ruling.court_name && <Badge>{ruling.court_name}</Badge>}
              {ruling.ruling_date && (
                <span className="text-xs text-ink-faint">{ruling.ruling_date}</span>
              )}
            </div>
            <h3 className="text-base font-bold text-ink-primary leading-snug">
              {ruling.title}
            </h3>
            {ruling.case_no && (
              <div className="text-xs text-ink-faint mt-0.5 font-mono">{ruling.case_no}</div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {b ? (
          <>
            {b.summary && (
              <section className="mb-3">
                <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-1">
                  {t('dailyBrief.ruling.summary', { defaultValue: '판결 요지' })}
                </div>
                <p className="text-sm text-ink-primary leading-relaxed">{b.summary}</p>
                {/* summary 약함 감지 — "정보만으로는" / "어렵습니다" 시작 시 안내 */}
                {(b.summary.startsWith('제공된 정보') ||
                  b.summary.includes('어렵습니다')) && (
                  <p className="mt-2 text-xs text-ink-faint italic">
                    ⓘ {t('dailyBrief.ruling.briefLimited', {
                      defaultValue: '판결문 본문이 아직 수집되지 않아 사건명 기반의 일반 해설입니다. 원문 링크에서 전문 확인을 권장합니다.',
                    })}
                  </p>
                )}
              </section>
            )}
            {b.key_finding && (
              <section className="mb-3 p-2 rounded border-l-2 border-brand-primary/60 bg-surface-elevated/30">
                <div className="text-xs font-semibold text-brand-primary uppercase tracking-wide mb-1">
                  ⚖ {t('dailyBrief.ruling.keyFinding', { defaultValue: '법원 판단 핵심' })}
                </div>
                <p className="text-sm text-ink-primary leading-relaxed font-medium">{b.key_finding}</p>
              </section>
            )}
            {b.implications && b.implications.length > 0 && (
              <section className="mb-3">
                <div className="text-xs font-semibold text-ink-secondary uppercase tracking-wide mb-1">
                  {t('dailyBrief.ruling.implications', { defaultValue: '안전관리자 시사점' })}
                </div>
                <ul className="space-y-1">
                  {b.implications.map((imp, i) => (
                    <li key={i} className="text-sm text-ink-primary leading-relaxed flex gap-1.5">
                      <span className="text-ink-faint">•</span>
                      <span className="flex-1">{imp}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {b.related_laws && b.related_laws.length > 0 && (
              <section className="mb-3 p-2 rounded bg-surface-elevated/40 border border-surface-elevated">
                <div className="text-xs font-semibold text-ink-secondary mb-1">
                  {t('dailyBrief.ruling.relatedLaws', { defaultValue: '관련 법령' })}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {b.related_laws.map((law, i) => (
                    <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-surface text-ink-primary">{law}</span>
                  ))}
                </div>
              </section>
            )}
            {b.practical_tip && (
              <section className="mb-3 p-2 rounded bg-brand-primary/8 border-l-2 border-brand-primary">
                <div className="text-xs font-semibold text-brand-primary mb-1 flex items-center gap-1">
                  <FiZap size={11} />
                  {t('dailyBrief.ruling.tip', { defaultValue: '오늘 할 일' })}
                </div>
                <p className="text-sm text-ink-primary">{b.practical_tip}</p>
              </section>
            )}
          </>
        ) : (
          <p className="text-sm text-ink-faint italic">
            {t('dailyBrief.ruling.briefPending', { defaultValue: 'AI 요약 준비 중 — 원문은 아래 링크에서 확인 가능합니다.' })}
          </p>
        )}

        <a
          href={ruling.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline mt-2"
        >
          <FiExternalLink size={11} />
          {t('dailyBrief.ruling.viewOriginal', { defaultValue: '판결문 원문 (국가법령정보센터)' })}
        </a>

        {ruling.source_attribution && (
          <div className="mt-3 pt-2 border-t border-surface-elevated/60 text-[11px] text-ink-faint leading-relaxed">
            {ruling.source_attribution}
          </div>
        )}
      </CardBody>
    </Card>
  )
}
